import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { formatINR } from "@/lib/format";
import { resolveMediaUrl, handleMediaError } from "@/lib/media";
import { toast } from "sonner";
import { Loader2, ArrowLeft, FileText, XCircle, Undo2 } from "lucide-react";

export default function AccountOrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [eligibility, setEligibility] = useState(null);

  const load = useCallback(() => api.get(`/orders/${id}`).then(({ data }) => setOrder(data.order)), [id]);
  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);

  useEffect(() => {
    if (order?.status === "DELIVERED") {
      api.get(`/orders/${id}/return-eligibility`).then(({ data }) => setEligibility(data.items)).catch(() => {});
    }
  }, [order?.status, id]);

  if (loading) return <div className="py-12 grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-amber-500" /></div>;
  if (!order) return <p className="text-slate-500">Order not found.</p>;

  const paid = order.paymentStatus === "PAID";
  const closed = ["CANCELLED", "REFUND_INITIATED", "REFUNDED"].includes(order.status);
  const cancellable = !closed && (paid || (order.paymentMethod === "COD" && ["ORDER_PLACED", "ORDER_CONFIRMED"].includes(order.status)));

  const cancel = async () => {
    if (!window.confirm("Cancel this order? A refund will be initiated if payment was taken.")) return;
    setBusy("cancel");
    try { await api.post(`/orders/${id}/cancel`, { reason: "Cancelled by customer" }); await load(); toast.success("Order cancelled"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const openInvoice = async () => {
    setBusy("invoice");
    try {
      const { data } = await api.get(`/orders/${id}/invoice`);
      const inv = data.invoice;
      const lines = (inv.items || []).map((i) => `<tr><td>${i.name}</td><td>${i.quantity}</td><td>${i.unitPrice}</td><td>${i.lineTotal}</td></tr>`).join("");
      const html = `<html><head><title>${inv.invoiceNumber}</title></head><body style="font-family:Arial;margin:32px">
        <h2>${inv.seller?.name || "Invoice"} · ${inv.invoiceNumber}</h2>
        <p>Order ${inv.orderNumber} · ${(inv.invoiceDate || "").slice(0, 10)}</p>
        <table border="1" cellpadding="6" cellspacing="0"><thead><tr><th>Item</th><th>Qty</th><th>Rate</th><th>Total</th></tr></thead><tbody>${lines}</tbody></table>
        <h3>Total: ₹${inv.totals?.total}</h3></body></html>`;
      const w = window.open("", "_blank");
      if (w) { w.document.write(html); w.document.close(); }
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };
  const requestReturn = async (item) => {
    setBusy(item.productId);
    try {
      await api.post(`/orders/${id}/returns`, {
        items: [{ productId: item.productId, variantId: item.variantId, quantity: 1, reason: "Customer request" }],
        reason: "Customer request",
      });
      toast.success("Return requested");
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  return (
    <div>
      <Link to="/account/orders" className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-4"><ArrowLeft className="h-4 w-4" /> Back to orders</Link>
      <div className="rounded-xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between flex-wrap gap-2 pb-4 border-b border-slate-200">
          <div><p className="text-xs text-slate-500">Order</p><p className="font-mono font-bold text-lg">{order.orderNumber}</p></div>
          <span className="text-xs font-semibold px-3 py-1.5 rounded-full bg-amber-50 text-amber-700">{order.status.replace(/_/g, " ")}</span>
        </div>
        <div className="py-4 space-y-3">
          {order.items.map((it) => (
            <div key={it.id} className="flex items-center gap-3">
              <img src={resolveMediaUrl(it.image)} alt="" onError={handleMediaError} className="h-14 w-14 rounded-lg bg-slate-50 object-contain" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 line-clamp-1">{it.name}</p>
                {it.attributes && Object.keys(it.attributes).length > 0 && <p className="text-xs text-slate-500">{Object.entries(it.attributes).map(([k, v]) => `${k}: ${v}`).join(" · ")}</p>}
                <p className="text-xs text-slate-500">Qty {it.quantity} · SKU {it.sku}</p>
              </div>
              <span className="font-mono text-sm">{formatINR(it.lineTotal)}</span>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-200 pt-4 grid sm:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Delivery Address</h4>
            <p className="text-sm text-slate-600">{order.address?.fullName}<br />{order.address?.line1}, {order.address?.city}, {order.address?.state} — {order.address?.pincode}<br />{order.address?.phone}</p>
          </div>
          <div className="text-sm space-y-1.5">
            <Row label="Subtotal" value={formatINR(order.subtotal)} />
            {order.discount > 0 && <Row label="Discount" value={`− ${formatINR(order.discount)}`} />}
            <Row label="Shipping" value={order.shippingCharge === 0 ? "Free" : formatINR(order.shippingCharge)} />
            <Row label="Payment method" value={order.paymentMethod === "COD" ? "Cash on Delivery" : "Razorpay"} />
            <Row label="Payment status" value={order.paymentStatus || "PENDING"} />
            {order.paymentMethod === "COD" && order.paymentStatus !== "PAID" &&
              <Row label="Due on delivery" value={formatINR(order.amountDue ?? order.codAmountDue ?? order.total)} />}
            {order.paymentMethod === "COD" && order.paymentCollection?.collectedAt &&
              <Row label="Collection recorded" value={new Date(order.paymentCollection.collectedAt).toLocaleString("en-IN")} />}
            <div className="flex justify-between font-semibold text-slate-900 pt-1 border-t border-slate-100"><span>Total</span><span className="font-mono">{formatINR(order.total)}</span></div>
          </div>
        </div>

        {(cancellable || paid) && (
          <div className="border-t border-slate-200 pt-4 mt-4 flex flex-wrap gap-3">
            {cancellable && (
              <button onClick={cancel} disabled={busy === "cancel"} data-testid="cancel-order-btn"
                className="inline-flex items-center gap-1.5 border border-slate-200 rounded-lg px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50">
                <XCircle className="h-4 w-4" /> Cancel order
              </button>
            )}
            {paid && (
              <button onClick={openInvoice} disabled={busy === "invoice"} data-testid="invoice-btn"
                className="inline-flex items-center gap-1.5 border border-slate-200 rounded-lg px-4 py-2 text-sm font-semibold text-slate-700 disabled:opacity-50">
                <FileText className="h-4 w-4" /> Invoice
              </button>
            )}
          </div>
        )}

        {eligibility && eligibility.length > 0 && (
          <div className="border-t border-slate-200 pt-4 mt-4">
            <h4 className="text-sm font-semibold text-slate-900 mb-2 inline-flex items-center gap-1.5">
              <Undo2 className="h-4 w-4" /> Returns
            </h4>
            <div className="space-y-2">
              {eligibility.map((e) => (
                <div key={`${e.productId}-${e.variantId}`} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-slate-600">{e.name}
                    {e.returnPolicy?.windowDays ? <span className="text-xs text-slate-400"> · {e.returnPolicy.windowDays}-day window</span> : null}
                  </span>
                  {e.eligible
                    ? <button onClick={() => requestReturn(e)} disabled={busy === e.productId}
                        className="text-xs font-semibold text-amber-600 disabled:opacity-50">Request return</button>
                    : <span className="text-xs text-slate-400">{e.reason}</span>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value }) => <div className="flex justify-between"><span className="text-slate-500">{label}</span><span className="font-mono text-slate-700">{value}</span></div>;
